import { useState, useEffect, useMemo, useCallback, Fragment } from "react";
import {
  useFetchTownQuery,
  useUpdateTownMutation,
  useDeleteTownMutation,
  useAddTownMutation,
} from "../../../store";
import { useFetchCityQuery } from "../../../store/storageRoles/cityApi";
import useAuth from "../../../Hooks/useAuth";
import usePermission from "../../../Hooks/usePermission";
import { useAlert } from "../../../context/AlertContext";
import { FaMapMarkerAlt, FaPlus, FaEdit, FaTrash } from "react-icons/fa";
import { ThreeDots } from "react-loader-spinner";
import { TextInput, Pagination } from "../../../Components/atoms";
import PageShell from "../../../Components/shared/PageShell";
import PageHeader from "../../../Components/shared/PageHeader";
import SearchInput from "../../../Components/shared/SearchInput";
import ListSkeleton from "../../../Components/shared/ListSkeleton";
import EmptyState from "../../../Components/shared/EmptyState";
import Modal from "../../../Components/shared/Modal";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";

const ITEMS_PER_PAGE = 15;
const SKELETON_COLS = [
  { label: "İlçe", skeletonWidth: "w-32" },
  { label: "Bağlı Olduğu Şehir", skeletonWidth: "w-28" },
  { label: "İşlemler", skeletonWidth: "w-20" },
];

export default function TownPage() {
  const { auth } = useAuth();
  const token = auth.accessToken;
  const { showSuccess, showError } = useAlert();
  const { hasPermission } = usePermission();
  const canCreate = hasPermission("parameters.townCreate");
  const canEdit = hasPermission("parameters.townEdit");
  const canDelete = hasPermission("parameters.townDelete");

  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedTown, setSelectedTown] = useState(null);
  const [townName, setTownName] = useState("");
  const [cityName, setCityName] = useState("");
  const [cityAutocompleteOpen, setCityAutocompleteOpen] = useState(false);

  const TownResult = useFetchTownQuery(token);
  const CityResult = useFetchCityQuery(token, { skip: !showModal });
  const [Update, resultUpdate] = useUpdateTownMutation();
  const [Delete, resultDelete] = useDeleteTownMutation();
  const [Add, resultAdd] = useAddTownMutation();

  const towns = useMemo(
    () => TownResult?.data?.data?.towns || [],
    [TownResult?.data?.data?.towns]
  );

  const cityOptions = useMemo(
    () => CityResult?.data?.data?.cities || [],
    [CityResult?.data?.data?.cities]
  );

  const filteredTowns = useMemo(() => {
    if (!searchTerm) return towns;
    const term = searchTerm.toLowerCase();
    return towns.filter(
      (t) =>
        t.townName?.toLowerCase().includes(term) ||
        t.city?.cityName?.toLowerCase().includes(term)
    );
  }, [towns, searchTerm]);

  const paginatedTowns = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredTowns.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredTowns, currentPage]);

  const totalPages = Math.ceil(filteredTowns.length / ITEMS_PER_PAGE);

  useEffect(() => setCurrentPage(1), [searchTerm]);

  const handleApiResult = useCallback(
    (result, successMsg) => {
      if (result.isSuccess) {
        showSuccess(successMsg);
        closeModals();
      }
      if (result.isError) {
        showError(result.error?.data?.message || "İşlem başarısız!");
      }
    },
    [showSuccess, showError]
  );

  useEffect(
    () => handleApiResult(resultAdd, "İlçe eklendi!"),
    [resultAdd.isSuccess, resultAdd.isError, handleApiResult]
  );
  useEffect(
    () => handleApiResult(resultUpdate, "İlçe güncellendi!"),
    [resultUpdate.isSuccess, resultUpdate.isError, handleApiResult]
  );
  useEffect(
    () => handleApiResult(resultDelete, "İlçe silindi!"),
    [resultDelete.isSuccess, resultDelete.isError, handleApiResult]
  );

  const closeModals = () => {
    setShowModal(false);
    setShowDeleteModal(false);
    setSelectedTown(null);
    setTownName("");
    setCityName("");
  };

  const openAddModal = () => {
    setSelectedTown(null);
    setTownName("");
    setCityName("");
    setShowModal(true);
  };

  const openEditModal = (town) => {
    setSelectedTown(town);
    setTownName(town.townName);
    setCityName(town.city?.cityName || town.cityName || "");
    setShowModal(true);
  };

  const openDeleteModal = (town) => {
    setSelectedTown(town);
    setShowDeleteModal(true);
  };

  const handleSave = () => {
    if (!townName.trim() || !cityName.trim()) return;
    if (selectedTown) {
      Update({ id: selectedTown._id, townName, cityName, token });
    } else {
      Add({ townName, cityName, token });
    }
  };

  const handleDelete = () => {
    Delete({ id: selectedTown._id, token });
  };

  const isLoading = TownResult.isFetching;

  return (
    <PageShell>
      <div className="p-3 sm:p-4 border-b border-gray-100 flex flex-col gap-3">
        <PageHeader
          icon={FaMapMarkerAlt}
          iconColor="emerald"
          title="İlçe Yönetimi"
          subtitle="İlçeleri görüntüleyin ve yönetin"
          stats={[
            { label: "Toplam", value: towns.length, color: "emerald" },
          ]}
          actions={canCreate ? [
            {
              label: "Yeni İlçe",
              icon: FaPlus,
              onClick: openAddModal,
              variant: "success",
            },
          ] : []}
          isLoading={isLoading}
        />

        <div className="flex items-center gap-2">
          <SearchInput
            placeholder="İlçe veya şehir ara..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            wrapperClassName="flex-1 max-w-sm"
          />
          {canCreate && (
            <button
              onClick={openAddModal}
              className="sm:hidden p-2.5 bg-emerald-600 text-white rounded-xl shadow-sm"
            >
              <FaPlus className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-auto">
        {isLoading ? (
          <ListSkeleton columns={SKELETON_COLS} rowCount={10} />
        ) : filteredTowns.length === 0 ? (
          <EmptyState
            icon={FaMapMarkerAlt}
            message="İlçe bulunamadı"
            suggestion={
              searchTerm
                ? "Farklı bir arama terimi deneyin."
                : "Henüz ilçe eklenmemiş."
            }
          />
        ) : (
          <>
            <div className="md:hidden p-3 space-y-2">
              {paginatedTowns.map((town) => (
                <div
                  key={town._id}
                  className="bg-white border border-gray-200 rounded-xl p-4 flex items-center justify-between"
                >
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {town.townName}
                    </p>
                    <p className="text-xs text-gray-500">
                      {town.city?.cityName}
                    </p>
                  </div>
                  {(canEdit || canDelete) && (
                    <div className="flex gap-1">
                      {canEdit && (
                        <button
                          onClick={() => openEditModal(town)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                        >
                          <FaEdit className="w-4 h-4" />
                        </button>
                      )}
                      {canDelete && (
                        <button
                          onClick={() => openDeleteModal(town)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                        >
                          <FaTrash className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="hidden md:block">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50 sticky top-0 z-10">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      İlçe
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Bağlı Olduğu Şehir
                    </th>
                    {(canEdit || canDelete) && (
                      <th className="px-6 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider w-32">
                        İşlemler
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {paginatedTowns.map((town) => (
                    <tr key={town._id} className="hover:bg-gray-50">
                      <td className="px-6 py-3 text-sm font-medium text-gray-900">
                        {town.townName}
                      </td>
                      <td className="px-6 py-3 text-sm text-gray-600">
                        {town.city?.cityName}
                      </td>
                      {(canEdit || canDelete) && (
                        <td className="px-6 py-3 text-right">
                          <div className="flex items-center justify-end gap-1">
                            {canEdit && (
                              <button
                                onClick={() => openEditModal(town)}
                                className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                              >
                                <FaEdit className="w-4 h-4" />
                              </button>
                            )}
                            {canDelete && (
                              <button
                                onClick={() => openDeleteModal(town)}
                                className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                              >
                                <FaTrash className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {!isLoading && filteredTowns.length > ITEMS_PER_PAGE && (
        <div className="border-t border-gray-100 p-3">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            itemsPerPage={ITEMS_PER_PAGE}
            totalItems={filteredTowns.length}
            showInfo
          />
        </div>
      )}

      {/* Add / Edit Modal */}
      <Modal open={showModal} onClose={closeModals} maxWidth="sm">
        <Modal.Header
          title={selectedTown ? "İlçe Düzenle" : "Yeni İlçe Ekle"}
          onClose={closeModals}
        />
        <Modal.Body className="space-y-4">
          <TextInput
            label="İlçe Adı"
            value={townName}
            onChange={(e) => setTownName(e.target.value)}
            placeholder="İlçe adını girin"
            required
          />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Bağlı Olduğu Şehir
            </label>
            <Autocomplete
              open={cityAutocompleteOpen}
              onOpen={() => setCityAutocompleteOpen(true)}
              onClose={() => setCityAutocompleteOpen(false)}
              isOptionEqualToValue={(option, value) =>
                option.cityName === value.cityName
              }
              onChange={(_, newValue) =>
                setCityName(newValue ? newValue.cityName : "")
              }
              value={
                cityOptions.find((c) => c.cityName === cityName) || null
              }
              getOptionLabel={(option) => option.cityName || ""}
              options={cityOptions}
              loading={CityResult.isLoading}
              size="small"
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Şehir seçin"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <Fragment>
                        {CityResult.isLoading && (
                          <CircularProgress color="inherit" size={18} />
                        )}
                        {params.InputProps.endAdornment}
                      </Fragment>
                    ),
                  }}
                />
              )}
            />
          </div>
        </Modal.Body>
        <Modal.Footer className="flex justify-end gap-3">
          <button
            onClick={closeModals}
            className="px-4 py-2 text-gray-600 hover:text-gray-800"
          >
            İptal
          </button>
          <button
            onClick={handleSave}
            disabled={resultAdd.isLoading || resultUpdate.isLoading}
            className="px-5 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-2"
          >
            {resultAdd.isLoading || resultUpdate.isLoading ? (
              <ThreeDots height={16} width={16} color="white" />
            ) : selectedTown ? (
              "Güncelle"
            ) : (
              "Ekle"
            )}
          </button>
        </Modal.Footer>
      </Modal>

      {/* Delete Modal */}
      <Modal open={showDeleteModal} onClose={closeModals} maxWidth="sm">
        <Modal.Header title="İlçe Sil" onClose={closeModals} />
        <Modal.Body>
          <p className="text-gray-600">
            <strong>{selectedTown?.townName}</strong> ilçesini silmek
            istediğinize emin misiniz?
          </p>
          <p className="text-sm text-red-600 mt-2">Bu işlem geri alınamaz!</p>
        </Modal.Body>
        <Modal.Footer className="flex justify-end gap-3">
          <button
            onClick={closeModals}
            className="px-4 py-2 text-gray-600 hover:text-gray-800"
          >
            İptal
          </button>
          <button
            onClick={handleDelete}
            disabled={resultDelete.isLoading}
            className="px-5 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
          >
            {resultDelete.isLoading ? (
              <ThreeDots height={16} width={16} color="white" />
            ) : (
              "Sil"
            )}
          </button>
        </Modal.Footer>
      </Modal>
    </PageShell>
  );
}
